import React, { useState } from 'react';
import { Menu, MenuItem, ListItemText, Typography } from '@mui/material';
import { toast } from 'react-toastify';
import caratulaRegistry from './base/caratulaRegistry';
import { extraerMensajeError } from '../../services/caratulaService';

function descargarBlob(data, nombreArchivo) {
    const url = window.URL.createObjectURL(new Blob([data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', nombreArchivo);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
}

/**
 * Botón "Generar Carátula" de cualquier módulo: qué modal abrir y contra qué endpoints
 * hablar sale de base/caratulaRegistry. El trámite ya tiene que existir, porque la carátula
 * se genera A PARTIR de él.
 *
 * Si el tipo declara `variantes` (Jubilación, Pensión, Reajuste) abre primero un menú y recién
 * con la elegida pide el prefill (GET .../datos?variante=). Al confirmar, genera y descarga el
 * .docx —que además queda adjuntado al trámite— y dispara `onGenerado` para que la ficha
 * refresque su lista de Archivos.
 *
 * `className` y `etiqueta` permiten que la ficha del trámite y el panel de Recepción usen el
 * mismo componente con su propio estilo.
 */
export default function BotonGenerarCaratula({
    tipo,
    idTramite,
    onGenerado,
    className = 'btn btn-outline-primary d-flex align-items-center gap-2 rounded-pill',
    etiqueta = 'Generar Carátula',
}) {
    const [anchorMenu, setAnchorMenu] = useState(null);
    const [cargandoDatos, setCargandoDatos] = useState(false);
    const [generando, setGenerando] = useState(false);
    const [modalAbierto, setModalAbierto] = useState(false);
    const [datos, setDatos] = useState(null);
    const [errorCarga, setErrorCarga] = useState(null);

    const config = caratulaRegistry[tipo];

    // Sin trámite no hay carátula, y un tipo sin plantilla (genérico) tampoco tiene nada que
    // emitir: en los dos casos el botón simplemente no existe.
    if (!idTramite || !config) {
        return null;
    }

    const ModalCaratula = config.modal;

    const abrirModal = async (variante) => {
        setAnchorMenu(null);
        setErrorCarga(null);
        // Sin esto el modal se abre hidratado con el documento anterior (o con la otra
        // variante): HojaCaratula copia `datos` apenas `abierto` pasa a true.
        setDatos(null);
        setModalAbierto(true);
        setCargandoDatos(true);
        try {
            const datosPrefill = await config.obtenerDatos(idTramite, variante);
            setDatos(datosPrefill);
        } catch (err) {
            console.error(`❌ Error obteniendo los datos de la Carátula ${config.etiqueta}:`, err);
            setErrorCarga('No se pudieron cargar los datos de la Carátula. Intente nuevamente.');
            setModalAbierto(false);
            toast.error('No se pudieron cargar los datos de la Carátula');
        } finally {
            setCargandoDatos(false);
        }
    };

    const handleClick = (e) => {
        if (config.variantes) {
            setAnchorMenu(e.currentTarget);
            return;
        }
        abrirModal();
    };

    const handleConfirmar = async (datosEditados) => {
        setGenerando(true);
        try {
            const response = await config.generar(idTramite, datosEditados);
            descargarBlob(response.data, config.nombreArchivo(datosEditados, idTramite));
            toast.success(`Datos guardados. ${config.mensajeDescarga}`);
            setModalAbierto(false);
            if (onGenerado) onGenerado();
        } catch (err) {
            // El mensaje del backend (ej. 400 "MAIL: ... no es un correo válido") va al toast y
            // a HojaCaratula, que lo muestra arriba de la hoja sin cerrar el modal.
            const mensaje = await extraerMensajeError(err);
            toast.error(mensaje);
            err.mensajeUsuario = mensaje;
            throw err;
        } finally {
            setGenerando(false);
        }
    };

    return (
        <>
            <button className={className} onClick={handleClick}>
                <i className="bi bi-file-earmark-text"></i>
                {etiqueta}
                {config.variantes && (
                    <i className="bi bi-chevron-down" style={{ fontSize: '0.75rem' }}></i>
                )}
            </button>

            {config.variantes && (
                <Menu
                    anchorEl={anchorMenu}
                    open={Boolean(anchorMenu)}
                    onClose={() => setAnchorMenu(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    PaperProps={{ sx: { borderRadius: '12px', mt: 0.5, minWidth: 280 } }}
                >
                    {config.variantes.map((v) => (
                        <MenuItem key={v.value} onClick={() => abrirModal(v.value)}>
                            <ListItemText
                                primary={v.titulo}
                                secondary={
                                    <Typography variant="caption" color="text.secondary">
                                        {v.detalle}
                                    </Typography>
                                }
                            />
                        </MenuItem>
                    ))}
                </Menu>
            )}

            {errorCarga && (
                <div className="alert alert-warning py-2 px-3 mt-2 mb-0 rounded-3" style={{ fontSize: '0.85rem' }}>
                    {errorCarga}
                </div>
            )}

            <ModalCaratula
                abierto={modalAbierto}
                cargandoDatos={cargandoDatos}
                datos={datos}
                generando={generando}
                onCancelar={() => setModalAbierto(false)}
                onConfirmar={handleConfirmar}
            />
        </>
    );
}
